import { memo, useCallback } from 'react';

import { CirclePauseIcon, CirclePlayIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { usePauseAllUploads, useResumeAllUploads } from '@air/upload-utils';

import { useWorkspaceUploadsInProgressCounts } from '~/components/v2/components/workspace/workspace-uploads/useWorkspaceUploadsInProgressCounts';

export type WorkspaceUploadsPauseResumeAllButtonProps = {
  isOnline: boolean;
};

export const WorkspaceUploadsPauseResumeAllButton = memo(({ isOnline }: WorkspaceUploadsPauseResumeAllButtonProps) => {
  const { pauseAllUploads } = usePauseAllUploads();
  const { resumeAllUploads } = useResumeAllUploads();
  const { hasProgressingUploads, isPaused } = useWorkspaceUploadsInProgressCounts();

  const handlePauseAll = useCallback(() => {
    void pauseAllUploads();
  }, [pauseAllUploads]);

  const handleResumeAll = useCallback(() => {
    resumeAllUploads();
  }, [resumeAllUploads]);

  if (isPaused && isOnline) {
    return (
      <Button
        appearance="ghost"
        className="ads:text-primary text-gray-12"
        color="gray"
        data-testid="WORKSPACE_UPLOADS_RESUME_ALL_BUTTON"
        onClick={handleResumeAll}
        prefix={<CirclePlayIcon aria-hidden size="xs" />}
        size="small"
        type="button"
      >
        Resume all
      </Button>
    );
  }

  if (hasProgressingUploads) {
    return (
      <Button
        appearance="ghost"
        className="ads:text-primary text-gray-12"
        color="gray"
        data-testid="WORKSPACE_UPLOADS_PAUSE_ALL_BUTTON"
        onClick={handlePauseAll}
        prefix={<CirclePauseIcon aria-hidden size="xs" />}
        size="small"
        type="button"
      >
        Pause all
      </Button>
    );
  }

  return null;
});

WorkspaceUploadsPauseResumeAllButton.displayName = 'WorkspaceUploadsPauseResumeAllButton';
